/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */

import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Vector3,
} from '@technology/web3d-plugin-threejs';
import { GeoJsonCollection } from '../expansion/features-buffer-geom-translator';
import { FeaturesServiceMeshData } from '../types/features-service-mesh-data';

export function constructGeometry(
  meshData: FeaturesServiceMeshData,
  mapBox: Box3, // The tile's bounding box in MAP coordinates
  mapToScene: (mapCoordinates: Vector3, result: Vector3) => Vector3,
  baseHeight: number,
  initialBaseHeight: number
): { geometry: BufferGeometry; localOrigin: Vector3 } {
  const { vertexData, header } = meshData as any;
  const triangleIndices = (meshData as any).triangleIndices;

  const elementsPerVertex = 3; // x, y, z

  const vertexCount = vertexData.length / elementsPerVertex;
  const positions = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);

  const mapRangeX = mapBox.max.x - mapBox.min.x;
  const mapRangeY = mapBox.max.y - mapBox.min.y;
  const heightRange = header.maxHeight - header.minHeight;

  const originMapCoord = new Vector3(
    mapBox.min.x,
    mapBox.min.y,
    header.minHeight
  );

  const originMaxCoord = new Vector3(
    mapBox.min.x + mapRangeX,
    mapBox.min.y + mapRangeY,
    header.maxHeight
  );

  const localOrigin = new Vector3();
  mapToScene(originMapCoord, localOrigin);

  const localMax = new Vector3();
  mapToScene(originMaxCoord, localMax);

  // Reusable vectors to avoid allocations in the loop
  const mapPosition = new Vector3();
  const scenePosition = new Vector3();

  for (let i = 0; i < vertexCount; i++) {
    const u = vertexData[i];
    const v = vertexData[i + vertexCount];
    const h = vertexData[i + vertexCount * 2];

    mapPosition.x = mapBox.min.x + (u / 32767.0) * mapRangeX;
    mapPosition.y = mapBox.min.y + (v / 32767.0) * mapRangeY;
    mapPosition.z = header.minHeight + (h / 32767.0) * heightRange;

    // If the base height has been adjusted from the initial value, apply the new
    // value to all vertices that were at the initial base height.
    if (mapPosition.z === initialBaseHeight) {
      mapPosition.z = baseHeight;
    }

    mapToScene(mapPosition, scenePosition);
    // Make the vertex position relative to the tile's local origin
    scenePosition.sub(localOrigin);

    // Store the final scene coordinate in the positions array
    const bufferIndex = i * 3;
    positions[bufferIndex] = scenePosition.x;
    positions[bufferIndex + 1] = scenePosition.y;
    positions[bufferIndex + 2] = mapPosition.z;

    uvs[i * 2] = u / 32767.0;
    uvs[i * 2 + 1] = v / 32767.0;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(triangleIndices, 1));
  geometry.computeVertexNormals();
  return { geometry, localOrigin };
}

export function approximateFeaturesServiceMeshDataSize(
  data: GeoJsonCollection
): number {
  // TODO: Write a better approximation

  const sizeInBytes = JSON.stringify(data).length;

  return sizeInBytes;
}
